import { ApolloError } from "@apollo/client";
import {
  CustomerEvalSuiteId,
  CustomerEvalSuiteVersionId,
  ExternalContextSource,
  MagicRulesFileId,
  OrgGuideFileId,
  OrgGuideFilePath,
  OrgGuideFileVersionId,
  OrgId,
  SemanticProjectFileId,
  SemanticProjectId,
  SemanticProjectVersionId,
  UserId,
} from "@hex/common";

import { LoadableState } from "../../../util/loadableState";

// --- Entity ---

export type ContextWorkbenchEntity =
  | ContextWorkbenchEntity.MagicRulesFile
  | ContextWorkbenchEntity.OrgGuideFile
  | ContextWorkbenchEntity.WorkspaceContextFile
  | ContextWorkbenchEntity.SemanticProjectFile
  | ContextWorkbenchEntity.EvalSuiteFile;

/**
 * A entity in the context workbench represents some piece of data that can be
 * uniquely identified by the client and server. This can be either single-player
 * or multi-player entities.
 */
export namespace ContextWorkbenchEntity {
  export type Type =
    | Type.MagicRulesFile
    | Type.OrgGuideFile
    | Type.WorkspaceContextFile
    | Type.SemanticProjectFile
    | Type.EvalSuiteFile;

  export namespace Type {
    /** @deprecated */
    export const MagicRulesFile = "MagicRulesFile" as const;
    /** @deprecated */
    export type MagicRulesFile = typeof Type.MagicRulesFile;

    export const OrgGuideFile = "OrgGuideFile" as const;
    export type OrgGuideFile = typeof Type.OrgGuideFile;

    export const WorkspaceContextFile = "WorkspaceContextFile" as const;
    export type WorkspaceContextFile = typeof Type.WorkspaceContextFile;

    export const SemanticProjectFile = "SemanticProjectFile" as const;
    export type SemanticProjectFile = typeof Type.SemanticProjectFile;

    export const EvalSuiteFile = "EvalSuiteFile" as const;
    export type EvalSuiteFile = typeof Type.EvalSuiteFile;
  }

  /** @deprecated */
  export type MagicRulesFile = {
    type: Type.MagicRulesFile;
    orgId: OrgId;
    userId: UserId | null;
    magicRulesFileId: MagicRulesFileId;
    filename: string;
    fileContent: string | null;
    sizeBytes: number | null;
  };

  /**
   * Entity shape for OrgGuideFile.
   */
  export type OrgGuideFile = {
    type: Type.OrgGuideFile;
    orgId: OrgId;
    orgGuideFileId: OrgGuideFileId;
    orgGuideFileVersionId: OrgGuideFileVersionId;
    filePath: OrgGuideFilePath;
    contents: string | null;
    externalSource: ExternalContextSource | null;
  };

  /**
   * Entity shape for WorkspaceContextFile.
   */
  export type WorkspaceContextFile = {
    type: Type.WorkspaceContextFile;
    orgId: OrgId;
    orgGuideFileId: OrgGuideFileId;
    orgGuideFileVersionId: OrgGuideFileVersionId;
    filePath: OrgGuideFilePath;
    contents: string | null;
    externalSource: ExternalContextSource | null;
  };

  /**
   * Entity shape for SemanticProjectFile.
   */
  export type SemanticProjectFile = {
    type: Type.SemanticProjectFile;
    orgId: OrgId;
    semanticProjectId: SemanticProjectId;
    semanticProjectVersionId: SemanticProjectVersionId;
    semanticProjectFileId: SemanticProjectFileId;
    filePath: string;
    contents: string | null;
  };

  /**
   * Entity shape for the raw definition of a single eval suite version.
   */
  export type EvalSuiteFile = {
    type: Type.EvalSuiteFile;
    orgId: OrgId;
    customerEvalSuiteId: CustomerEvalSuiteId;
    customerEvalSuiteVersionId: CustomerEvalSuiteVersionId;
    filePath: string;
    contents: string | null;
    externalSource: ExternalContextSource | null;
  };
}

// --- Entities aggregate ---

/**
 * An object that acts as the source of truth for single-player entities within
 * the context workbench. Note that only `magicRulesFile` are included here, since
 * `orgGuideFile`s are stored in a separate MP slice.
 */
export type ContextWorkbenchEntities = {
  magicRulesFile: LoadableState<
    ContextWorkbenchEntity.MagicRulesFile,
    ApolloError
  >;
};

export function getInitialContextWorkbenchEntities(): ContextWorkbenchEntities {
  return {
    magicRulesFile: {
      loading: false,
      error: undefined,
      value: undefined,
    },
  };
}

// --- EntityRef ---

export type ContextWorkbenchEntityRef =
  | ContextWorkbenchEntityRef.MagicRulesFile
  | ContextWorkbenchEntityRef.OrgGuideFile
  | ContextWorkbenchEntityRef.WorkspaceContextFile
  | ContextWorkbenchEntityRef.SemanticProjectFile
  | ContextWorkbenchEntityRef.EvalSuiteFile;

/**
 * A lightweight pointer to an entity that's often embedded in other data
 * structures. It contains exactly enough information to fetch the full entity
 * from its respective source of truth.
 */
export namespace ContextWorkbenchEntityRef {
  /** @deprecated */
  export type MagicRulesFile = {
    type: ContextWorkbenchEntity.Type.MagicRulesFile;
    orgId: OrgId;
    userId: UserId | null;
    magicRulesFileId: MagicRulesFileId;
  };

  export type OrgGuideFile = {
    type: ContextWorkbenchEntity.Type.OrgGuideFile;
    orgId: OrgId;
    orgGuideFileId: OrgGuideFileId;
    orgGuideFileVersionId: OrgGuideFileVersionId;
  };

  export type WorkspaceContextFile = {
    type: ContextWorkbenchEntity.Type.WorkspaceContextFile;
    orgId: OrgId;
    orgGuideFileId: OrgGuideFileId;
    orgGuideFileVersionId: OrgGuideFileVersionId;
  };

  export type SemanticProjectFile = {
    type: ContextWorkbenchEntity.Type.SemanticProjectFile;
    orgId: OrgId;
    semanticProjectId: SemanticProjectId;
    semanticProjectVersionId: SemanticProjectVersionId;
    semanticProjectFileId: SemanticProjectFileId;
  };

  export type EvalSuiteFile = {
    type: ContextWorkbenchEntity.Type.EvalSuiteFile;
    orgId: OrgId;
    customerEvalSuiteId: CustomerEvalSuiteId;
    customerEvalSuiteVersionId: CustomerEvalSuiteVersionId;
  };
}
